import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { useLive } from "@/lib/useLive";
import { CLP, formatDateTime, formatDate, formatTime, formatDateNumeric } from "@/lib/format";
import { CheckoutDialog } from "@/components/CheckoutDialog";
import { GroupSessionPicker } from "@/components/GroupSessionPicker";
import { useMyWorkshopStatus } from "@/lib/myWorkshopStatus";
import { Link as RLink } from "react-router-dom";
import { PhoneInput, isValidPhone } from "@/components/PhoneInput";
import { useAuth } from "@/context/AuthContext";
import { PhotoMosaic } from "@/components/PhotoMosaic";
import { AskMaestroActions } from "@/components/AskMaestroActions";
import { ReportMaestroButton } from "@/components/ReportMaestro";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import {
  Star, MapPin, Clock, Users, Check, ArrowLeft, Gift, CalendarClock, CalendarDays, Bell, Loader2, Navigation, Heart, MessageCircle, Send, Eye, Info,
} from "lucide-react";

export default function WorkshopDetail() {
  const { id } = useParams();
  useEffect(() => { window.scrollTo(0, 0); }, [id]);
  const { user } = useAuth();
  const [w, setW] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [qaOpen, setQaOpen] = useState(false);
  const [qaThread, setQaThread] = useState(null);
  const [qaText, setQaText] = useState("");
  const [qaLoading, setQaLoading] = useState(false);
  const mine = useMyWorkshopStatus();
  const [giftOnly, setGiftOnly] = useState(false);
  const [pick, setPick] = useState({ groupId: null, sessionIds: [] });
  const isOwner = !!(user && w && (w.business_id === user.owned_business_id || w.business_id === user.id));
  const blocked = isOwner ? "owner" : w && w.active === false ? "inactive" : user && user.role !== "customer" ? "mode" : null;

  const load = () => api.get(`/workshops/${id}`).then((r) => {
    setW(r.data);
    const first = r.data.sessions?.find((s) => s.spots_left > 0) || r.data.sessions?.[0];
    setSelectedSession(first || null);
    const gs = r.data.groups || [];
    setPick({ groupId: r.data.enrollment_mode !== "free" && gs.length === 1 ? gs[0].id : null, sessionIds: [] });
  });
  useEffect(() => { load(); }, [id]);
  useLive(() => api.get(`/workshops/${id}`).then((r) => {
    setW(r.data);
    setSelectedSession((prev) => (prev && r.data.sessions?.find((s) => s.id === prev.id)) || prev);
  }), 10000, !!w);
  useEffect(() => {
    if (w && !blocked && new URLSearchParams(window.location.search).get("pagar") === "1") {
      window.history.replaceState(null, "", window.location.pathname);
      setGiftOnly(false); setCheckoutOpen(true);
    }
  }, [w, blocked]);

  const saveForLater = async () => {
    try {
      const { data } = await api.post("/saved", { workshop_id: w.id });
      toast.success(data.already_saved ? "Ya estaba en Mi Lista de Deseos" : "Agregado a Mi Lista de Deseos");
      mine.reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openQa = async () => {
    if (!user) { toast.error("Inicia sesión como alumno para escribirle al Maestro"); return; }
    if (user.role !== "customer") { toast.error("Solo los alumnos pueden preguntar al Maestro"); return; }
    setQaOpen(true);
    try {
      const { data } = await api.get(`/workshops/${id}/questions/me`);
      setQaThread(data);
    } catch { setQaThread(null); }
  };

  const sendQuestion = async () => {
    if (!qaText.trim()) return;
    setQaLoading(true);
    try {
      const { data } = await api.post(`/workshops/${id}/questions`, { text: qaText.trim() });
      setQaThread(data.thread);
      setQaText("");
      toast.success("Tu pregunta fue enviada al Maestro");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setQaLoading(false); }
  };

  if (!w) return <div className="min-h-[60vh] grid place-items-center text-muted-foreground">Cargando…</div>;

  const soldOut = w && w.spots_left <= 0;
  const pickGroup = w?.groups?.find((g) => g.id === pick.groupId);
  const pickCount = w?.enrollment_mode === "free" ? pick.sessionIds.length : (pickGroup?.sessions.length || 0);
  const freeMax = w?.enrollment_mode === "free"
    ? (Math.min(w.max_sessions || 1, (w.groups || []).reduce((n, x) => n + x.sessions.filter((s) => s.spots_left > 0).length, 0)) || 1) : 0;
  const giftReady = freeMax ? pickCount === freeMax : pickCount > 0;
  const img = fileUrl(w.images?.[0]) || "https://images.unsplash.com/photo-1522065893269-6fd20f6d7438?q=85&w=1200";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Link to={`/${(() => { try { return JSON.parse(sessionStorage.getItem("catalog-return"))?.search || ""; } catch { return ""; } })()}`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-link">
        <ArrowLeft className="w-4 h-4" /> Volver al catálogo
      </Link>

      <div className="grid lg:grid-cols-3 gap-8 [&>*]:min-w-0">
        {/* Left */}
        <div className="lg:col-span-2 space-y-8">
          {/* Gallery */}
          <div className="w-full max-w-[600px] rounded-2xl overflow-hidden h-[300px] sm:h-[420px] shadow-sm hover:shadow-[0_14px_36px_-14px_rgba(121,68,228,0.35)] transition-shadow duration-300">
            <div className="relative h-full">
              <PhotoMosaic images={w.images} alt={w.name} className="w-full h-full" />
              {w.students_count > 0 && (
                <span className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur-md px-3 py-1.5 text-xs font-semibold text-white shadow" data-testid="workshop-students-count">
                  <Users className="w-3.5 h-3.5" />{w.students_count} {w.students_count === 1 ? "alumno lo ha realizado" : "alumnos lo han realizado"}
                </span>
              )}
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <Badge>{w.category}</Badge>
              {w.level && <Badge variant="outline">{w.level}</Badge>}
              {w.rating != null && (
                <span className="flex items-center gap-1 text-sm font-semibold" data-testid="workshop-thumbs">
                  <Star className="w-4 h-4 fill-amber-400 text-amber-400" />{Number(w.rating).toFixed(1)}
                  <span className="text-muted-foreground font-normal">· {w.reviews_count} {w.reviews_count === 1 ? "evaluación" : "evaluaciones"}</span>
                </span>
              )}
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{w.name}</h1>
            <div className="mt-1 flex items-center gap-1.5">
              <Link to={`/maestro/${w.business_id}`} className="text-primary font-medium hover:underline" data-testid="maestro-link">{w.business_name}</Link>
              <span className="relative group">
                <Link to={`/maestro/${w.business_id}`} aria-label="Ver perfil del Maestro" data-testid="maestro-eye-link"
                  className="grid place-items-center w-7 h-7 rounded-full text-primary hover:bg-primary/10 transition-colors">
                  <Eye className="w-4 h-4" />
                </Link>
                <span role="tooltip" data-testid="maestro-eye-tooltip" className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1 z-20 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-xs font-medium text-background shadow-md opacity-0 group-hover:opacity-100 group-active:opacity-100 transition-opacity">Ver perfil del Maestro</span>
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{[w.business_address, w.business_commune].filter(Boolean).join(", ") || w.business_commune}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />Cada sesión de {w.duration_min} min</span>
              {w.level && <span className="flex items-center gap-1.5"><Users className="w-4 h-4" />Nivel {w.level}</span>}
            </div>
            {w.next_session?.starts_at && (
              <p className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary" data-testid="workshop-start-date">
                <CalendarDays className="w-4 h-4" />Inicia el {formatDateNumeric(w.next_session.starts_at)}
                {(w.sessions?.length || 0) > 1
                  ? <span data-testid="workshop-end-date"> · Termina el {formatDateNumeric(w.sessions[w.sessions.length - 1].starts_at)}</span>
                  : <span className="font-normal" data-testid="workshop-single-session">(única sesión)</span>}
              </p>
            )}
            {(w.business_waze_url || w.business_maps_url) && (
              <div className="mt-4 flex flex-wrap gap-2" data-testid="directions">
                {w.business_waze_url && (
                  <a href={w.business_waze_url} target="_blank" rel="noopener noreferrer" data-testid="waze-link">
                    <Button variant="outline" size="sm" className="gap-2">
                      <Navigation className="w-4 h-4 text-primary" /> Ir con Waze
                    </Button>
                  </a>
                )}
                {w.business_maps_url && (
                  <a href={w.business_maps_url} target="_blank" rel="noopener noreferrer" data-testid="maps-link">
                    <Button variant="outline" size="sm" className="gap-2">
                      <MapPin className="w-4 h-4 text-primary" /> Google Maps
                    </Button>
                  </a>
                )}
              </div>
            )}
            {w.business_address_reference && (
              <p className="mt-2 text-xs text-muted-foreground" data-testid="address-reference">Referencia: {w.business_address_reference}</p>
            )}
            <p className="mt-5 text-base leading-relaxed">{w.description}</p>
          </div>

          {(w.includes?.length > 0 || w.bring?.length > 0) && (
            <div className="grid sm:grid-cols-2 gap-6">
              {w.includes?.length > 0 && (
                <Card className="p-5">
                  <h3 className="font-semibold mb-3">Qué incluye</h3>
                  <ul className="space-y-2 text-sm">
                    {w.includes.map((it, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />{it}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
              {w.bring?.length > 0 && (
                <Card className="p-5">
                  <h3 className="font-semibold mb-3">Qué traer</h3>
                  <ul className="space-y-2 text-sm">
                    {w.bring.map((it, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-accent mt-2 shrink-0" />{it}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </div>
          )}

          {/* Gallery from past workshops */}
          {w.gallery?.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">Proyectos de alumnos anteriores</h3>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {w.gallery.slice(0, 8).map((p, i) => (
                  <img key={i} src={fileUrl(p)} alt="" className="aspect-square object-cover rounded-lg" />
                ))}
              </div>
            </div>
          )}

          {/* Reviews */}
          {w.reviews?.length > 0 && (
            <div>
              <h3 className="font-semibold mb-4 text-xl">Lo que dicen los asistentes</h3>
              <div className="space-y-4">
                {w.reviews.map((r) => (
                  <Card key={r.id} className="p-5" data-testid={`review-${r.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{r.customer_name}</span>
                      {(r.vote ? r.vote === "up" : (r.rating || 0) >= 4)
                        ? <ThumbsUp className="w-5 h-5 text-emerald-600" aria-label="Le gustó" />
                        : <ThumbsDown className="w-5 h-5 text-rose-600" aria-label="No le gustó" />}
                    </div>
                    {r.comment && <p className="text-sm text-muted-foreground">{r.comment}</p>}
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: sticky checkout */}
        <div className="lg:col-span-1">
          <Card className="p-6 lg:sticky lg:top-24 space-y-5" data-testid="checkout-sidebar">
            <div>
              {w.offer_active ? (
                <div data-testid="detail-offer">
                  <div className="flex items-center gap-2">
                    {w.effective_price_clp === 0 ? (
                      <span className="font-display font-extrabold text-3xl text-emerald-600" data-testid="detail-free">GRATIS</span>
                    ) : (<>
                      <span className="font-display font-extrabold text-3xl text-primary">{CLP(w.effective_price_clp)}</span>
                      <span className="inline-flex items-center rounded-full bg-primary/15 text-primary text-xs font-bold px-2 py-0.5">
                        {w.offer?.discount_type === "fixed" ? "OFERTA" : `-${w.offer?.discount_value}% OFF`}
                      </span>
                    </>)}
                  </div>
                  <span className="text-muted-foreground text-sm line-through">{CLP(w.price_clp)}</span>
                  <span className="text-muted-foreground text-sm"> / persona</span>
                  {w.offer?.ends_at && <p className="text-xs text-primary mt-1 font-medium">Oferta válida hasta el {formatDateNumeric(w.offer.ends_at)}</p>}
                </div>
              ) : (
                <>
                  <span className="font-display font-extrabold text-3xl">{CLP(w.price_clp)}</span>
                  <span className="text-muted-foreground text-sm"> / persona</span>
                </>
              )}
            </div>

            <div>
              <p className="text-sm font-medium mb-2 flex items-center gap-1.5">
                <CalendarClock className="w-4 h-4" /> {w.enrollment_mode === "free" ? "Elige tus sesiones" : (w.groups?.length || 0) > 1 ? "Elige tu grupo" : "Itinerario del taller"}
              </p>
              <GroupSessionPicker w={w} value={pick} onChange={setPick} />
              <p className="text-xs mt-2" data-testid="workshop-spots">
                {soldOut ? <span className="text-destructive font-medium">Agotado</span>
                  : <span className="text-muted-foreground">Hasta {w.spots_left} cupos disponibles{w.enrollment_mode === "free" ? " por sesión" : (w.groups?.length || 0) > 1 ? " por grupo" : ""}</span>}
              </p>
            </div>

            <Separator />

            {w.gift_enabled && (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Gift className="w-4 h-4 text-accent" /> Disponible para regalar 🎁
              </p>
            )}

            {blocked ? (
              <div className="space-y-2" data-testid="reserve-blocked-box">
                <div className="flex items-start gap-2 rounded-xl border border-primary/25 bg-primary/5 p-3 text-sm" data-testid="reserve-blocked-msg">
                  <Info className="w-5 h-5 shrink-0 text-primary mt-0.5" />
                  <span>{blocked === "owner"
                    ? <><strong>Este es tu taller.</strong> Así lo ven los alumnos; tú no puedes reservarlo, agregarlo a tu Lista de Deseos ni enviarte preguntas.</>
                    : blocked === "inactive"
                    ? <><strong>Este taller no está disponible por ahora.</strong> No se pueden hacer nuevas reservas.</>
                    : <><strong>Estás en Modo Maestro.</strong> Para reservar o guardar talleres, cambia a Modo Alumno desde el menú de tu cuenta.</>}</span>
                </div>
                <Button className="w-full h-12 text-base" disabled data-testid="reserve-btn-disabled">Reservar</Button>
                <Button variant="outline" className="w-full gap-2" disabled data-testid="save-later-btn-disabled">
                  <Heart className="w-4 h-4 text-coral" /> Agregar a Mi Lista de Deseos
                </Button>
              </div>
            ) : w && mine.paid.has(w.id) ? (
              <div className="space-y-2" data-testid="already-paid-box">
                <div className="flex items-center gap-2 rounded-xl border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800">
                  <Check className="w-5 h-5 shrink-0" /><span><strong>Ya reservaste este taller.</strong> Tu ticket está en Mis Tickets.</span>
                </div>
                <Button asChild className="w-full h-12 text-base"><RLink to="/mis-tickets" data-testid="view-my-ticket-btn">Ver mi ticket</RLink></Button>
                {w.gift_enabled && !soldOut && (
                  <Button variant="outline" className="w-full gap-2" disabled={!giftReady} onClick={() => { setGiftOnly(true); setCheckoutOpen(true); }} data-testid="gift-again-btn">
                    <Gift className="w-4 h-4 text-accent" /> Regalar este taller
                  </Button>
                )}
              </div>
            ) : soldOut ? (
              <Button variant="outline" className="w-full h-12 gap-2" onClick={() => setWaitlistOpen(true)} data-testid="join-waitlist-btn">
                <Bell className="w-4 h-4" /> Unirme a la lista de espera
              </Button>
            ) : (
              <Button className="w-full h-12 text-base" disabled={!giftReady}
                onClick={() => { setGiftOnly(false); setCheckoutOpen(true); }} data-testid="reserve-btn">
                {freeMax ? `Reservar (${pickCount} de ${freeMax})` : pickCount ? `Reservar (${pickCount} ${pickCount === 1 ? "sesión" : "sesiones"})` : "Elige un grupo"}
              </Button>
            )}
            {!blocked && user && user.role === "customer" && !mine.paid.has(w.id) && (mine.saved.has(w.id) ? (
              <Button variant="outline" className="w-full gap-2" disabled data-testid="already-saved-btn">
                <Heart className="w-4 h-4 text-coral fill-current" /> Ya está en tu Lista de Deseos
              </Button>
            ) : (
              <Button variant="outline" className="w-full gap-2" onClick={saveForLater} data-testid="save-later-btn">
                <Heart className="w-4 h-4 text-coral" /> Agregar a Mi Lista de Deseos
              </Button>
            ))}
            {!blocked && <AskMaestroActions workshop={w} onInternal={openQa} />}
            {!isOwner && w.business_id && <div className="pt-1"><ReportMaestroButton businessId={w.business_id} maestroName={w.business_name} /></div>}
            <p className="text-xs text-center text-muted-foreground">Reserva gratis · acceso a todas las sesiones · Ticket con QR</p>
          </Card>
        </div>
      </div>

      <Dialog open={qaOpen} onOpenChange={setQaOpen}>
        <DialogContent className="max-w-md" data-testid="qa-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><MessageCircle className="w-5 h-5 text-primary" /> Pregúntale al Maestro</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {qaThread?.messages?.length > 0 && (
              <div className="max-h-56 overflow-y-auto space-y-2 rounded-lg bg-muted/40 p-3" data-testid="qa-thread">
                {qaThread.messages.map((m, i) => (
                  <div key={i} className={m.sender === "alumno" ? "text-right" : "text-left"}>
                    <span className={`inline-block rounded-2xl px-3 py-1.5 text-sm ${m.sender === "alumno" ? "bg-primary text-primary-foreground" : "bg-background border border-border"}`}>
                      {m.text}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <Textarea value={qaText} onChange={(e) => setQaText(e.target.value)} rows={3}
              placeholder="Escribe tu duda sobre horarios, materiales, cupos…" data-testid="qa-input" />
            <Button className="w-full gap-2" onClick={sendQuestion} disabled={qaLoading || !qaText.trim()} data-testid="qa-send">
              {qaLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar pregunta
            </Button>
            <p className="text-xs text-muted-foreground text-center">El Maestro recibirá tu consulta y podrá responderte pronto.</p>
          </div>
        </DialogContent>
      </Dialog>

      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} workshop={w} giftOnly={giftOnly}
        selection={{ group_id: w.enrollment_mode === "free" ? null : pick.groupId, session_ids: w.enrollment_mode === "free" ? pick.sessionIds : null, count: pickCount, label: pickGroup?.name }} />
      <WaitlistDialog open={waitlistOpen} onOpenChange={setWaitlistOpen} workshop={w} />
    </div>
  );
}

function WaitlistDialog({ open, onOpenChange, workshop }) {
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "" });
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (!form.name || !form.email || !form.whatsapp) { toast.error("Completa todos los campos"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/waitlist", { workshop_id: workshop.id, customer: form });
      toast.success(`${data.message} (posición #${data.position})`);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="waitlist-dialog">
        <DialogHeader><DialogTitle>Lista de espera</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Te avisaremos por WhatsApp apenas se libere un cupo, con 15 min de reserva exclusiva.</p>
        <div className="space-y-3 mt-2">
          <Input placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="wl-name" />
          <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="wl-email" />
          <PhoneInput value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} testId="wl-whatsapp" />
          <Button className="w-full" onClick={submit} disabled={loading || !isValidPhone(form.whatsapp)} data-testid="wl-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Avísame"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
